import React, { useState, useEffect } from 'react'
import { Link as ScrollLink } from 'react-scroll'
import ThemeToggle from './ThemeToggle'

const NAV_ITEMS = [
  { id: 1, path: 'home', label: 'Home' },
  { id: 2, path: 'posts', label: 'Posts' },
  { id: 3, path: 'followme', label: 'Follow' },
  { id: 4, path: 'work', label: 'Work' },
  { id: 5, path: 'skills', label: 'Skills' },
  { id: 6, path: 'contact', label: 'Contact' }
]

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('home')

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  return (
    <>
      <header className={`navbar px-5 md:px-10 lg:px-16 ${scrolled ? 'scrolled' : ''}`}>
        <div className='max-content w-full flex items-center justify-between'>
          <ScrollLink
            to='home'
            smooth
            duration={600}
            className='flex items-center gap-3 cursor-pointer group select-none'
            onClick={() => setIsMenuOpen(false)}
          >
            <div
              className='w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold'
              style={{
                background: 'linear-gradient(135deg, #63d3b7, #38bdf8)',
                color: '#ffffff',
                fontFamily: 'var(--font-display)',
                letterSpacing: '-0.02em'
              }}
            >
              AI
            </div>
            <span
              className='font-display font-semibold text-base tracking-tight'
              style={{ color: 'var(--text-primary)' }}
            >
              Albin<span style={{ color: 'var(--accent-teal)' }}> Issac</span>
            </span>
          </ScrollLink>

          <nav className='hidden md:flex items-center gap-1' aria-label='Main navigation'>
            {NAV_ITEMS.map(item => (
              <ScrollLink
                key={item.id}
                to={item.path}
                smooth
                duration={600}
                spy
                onSetActive={() => setActiveSection(item.path)}
                href={`#${item.path}`}
                className={`animated-underline px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors duration-200 ${
                  activeSection === item.path
                    ? 'text-[var(--accent-teal)]'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                {item.label}
              </ScrollLink>
            ))}
            <div className='ml-2'>
              <ThemeToggle />
            </div>
            <ScrollLink
              to='contact'
              smooth
              duration={600}
              className='ml-2 btn-primary text-sm cursor-pointer'
            >
              Get in touch
            </ScrollLink>
          </nav>

          <div className='md:hidden flex items-center gap-2'>
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(v => !v)}
              className='flex flex-col justify-center items-center w-10 h-10 gap-1.5 rounded-lg border transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400'
              style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-glass)' }}
              data-testid='toggle-menu'
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <span data-testid={isMenuOpen ? 'close-icon' : 'hamburger-icon'} className='sr-only'>
                {isMenuOpen ? 'Close' : 'Open'}
              </span>
              <span
                className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}
                style={{ background: 'var(--text-secondary)' }}
              />
              <span
                className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}
                style={{ background: 'var(--text-secondary)' }}
              />
              <span
                className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}
                style={{ background: 'var(--text-secondary)' }}
              />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: '68px' }}
      >
        <div
          className='absolute inset-0'
          style={{ background: 'var(--mobile-overlay)', backdropFilter: 'blur(16px)' }}
          onClick={() => setIsMenuOpen(false)}
        />
        <nav className='relative flex flex-col gap-2 p-6 pt-8' aria-label='Mobile navigation'>
          {NAV_ITEMS.map((item, i) => (
            <ScrollLink
              key={item.id}
              to={item.path}
              smooth
              duration={600}
              onClick={() => setIsMenuOpen(false)}
              href={`#${item.path}`}
              data-testid={`mobile-menu-${item.label}`}
              className='flex items-center justify-between px-5 py-4 rounded-xl text-base font-medium cursor-pointer transition-all duration-200 animate-fade-up'
              style={{
                animationDelay: `${i * 50}ms`,
                borderColor:
                  activeSection === item.path ? 'var(--border-accent)' : 'var(--border-subtle)',
                border: '1px solid',
                background:
                  activeSection === item.path ? 'rgba(99,211,183,0.08)' : 'var(--bg-glass)',
                color: activeSection === item.path ? 'var(--accent-teal)' : 'var(--text-primary)'
              }}
            >
              {item.label}
              <span style={{ color: 'var(--text-muted)' }}>→</span>
            </ScrollLink>
          ))}
          <div className='mt-4'>
            <ScrollLink
              to='contact'
              smooth
              duration={600}
              onClick={() => setIsMenuOpen(false)}
              className='btn-primary w-full justify-center text-sm cursor-pointer'
            >
              Get in touch
            </ScrollLink>
          </div>
        </nav>
      </div>
    </>
  )
}

export default Navbar
